import { useState } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import DiagnosticEvidenceView from './DiagnosticEvidenceView';

const severityColors = {
  critical: 'bg-red-600/20 text-red-400 border-red-500/30',
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

const severityLabels = {
  critical: { en: 'Critical', cs: 'Kritická' },
  high: { en: 'High', cs: 'Vysoká' },
  medium: { en: 'Medium', cs: 'Střední' },
  low: { en: 'Low', cs: 'Nízká' }
};

const causeLabels = {
  'redirect-chain': { en: 'Redirect Chain', cs: 'Řetěz přesměrování' },
  'slow-dns': { en: 'Slow DNS Lookup', cs: 'Pomalé DNS' },
  'slow-tcp': { en: 'Slow TCP Connection', cs: 'Pomalé TCP spojení' },
  'slow-tls': { en: 'Slow TLS Handshake', cs: 'Pomalý TLS handshake' },
  'slow-backend': { en: 'Slow Backend', cs: 'Pomalý backend' },
  'slow-request-wait': { en: 'Long request wait (backend not isolated)', cs: 'Dlouhé čekání na odpověď (backend nerozlišen)' },
  'http1': { en: 'Using HTTP/1.1', cs: 'Používá HTTP/1.1' },
  'no-cache': { en: 'No Caching', cs: 'Bez cache' },
  'no-keepalive': { en: 'No Keep-Alive', cs: 'Bez keep-alive' },
  'large-document': { en: 'Large Document', cs: 'Velký dokument' }
};

const phaseColors = {
  redirect: '#ef4444',
  dns: '#f59e0b',
  tcp: '#10b981',
  tls: '#6366f1',
  request: '#8b5cf6',
  response: '#ec4899'
};

export const TTFBDiagnosticsPanel = ({ url, user, onResultsChange }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [multiPageLoading, setMultiPageLoading] = useState(false);
  const [multiPageResults, setMultiPageResults] = useState(null);
  const [expandedPages, setExpandedPages] = useState({});
  
  const hasDeepAccess = user?.tier === 'premium' || user?.tier === 'enterprise' || user?.is_admin;

  const runDiagnostics = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/lab-audits/ttfb-diagnostics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setResults(data.data);
        onResultsChange?.(data.data);
      } else {
        setError(data.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('TTFB diagnostics error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runMultiPageAnalysis = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setMultiPageLoading(true);
    
    try {
      const response = await fetch('/api/lab-audits/multi-page-ttfb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success) {
        setMultiPageResults(data);
      }
    } catch (err) {
      console.error('Multi-page TTFB error:', err);
    } finally {
      setMultiPageLoading(false);
    }
  };

  const resetResults = () => {
    setResults(null);
    onResultsChange?.(null);
    setError(null);
    setMultiPageResults(null);
    setExpandedPages({});
  };

  const togglePageExpanded = (idx) => {
    setExpandedPages(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const formatMs = (ms) => {
    if (ms == null || !Number.isFinite(Number(ms)) || ms < 0) return language === 'cs' ? 'nedostupné' : 'unavailable';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.round(ms)}ms`;
  };

  const getTTFBStatus = (ttfb) => {
    if (ttfb <= 800) return { status: 'good', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (ttfb <= 1800) return { status: 'needs-improvement', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { status: 'poor', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const renderPhaseBar = () => {
    if (!results?.phases) return null;
    
    const phases = results.phases;
    const total = Object.values(phases).reduce((sum, v) => sum + (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0), 0);

    const phaseLabels = {
      redirect: language === 'cs' ? 'Redirect' : 'Redirect',
      dns: 'DNS',
      tcp: 'TCP',
      tls: 'TLS',
      request: language === 'cs' ? 'Server' : 'Server',
      response: language === 'cs' ? 'Odpověď' : 'Response'
    };

    return (
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-400">
          {language === 'cs' ? 'Fázový breakdown' : 'Phase Breakdown'}
        </div>
        {total > 0 && <div className="h-6 flex rounded-lg overflow-hidden">
          {Object.entries(phases).map(([phase, value]) => {
            if (!value || value <= 0) return null;
            const percent = (value / total) * 100;
            return (
              <div 
                key={phase}
                className="flex items-center justify-center text-[10px] text-white font-medium"
                style={{ 
                  width: `${Math.max(percent, 8)}%`,
                  backgroundColor: phaseColors[phase]
                }}
                title={`${phaseLabels[phase]}: ${formatMs(value)}`}
              >
                {percent > 15 && formatMs(value)}
              </div>
            );
          })}
        </div>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {Object.entries(phases).map(([phase, value]) => {
            return (
              <div key={phase} className="flex items-center gap-1">
                <span 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: phaseColors[phase] }}
                />
                <span className="text-slate-400">{phaseLabels[phase]}:</span>
                <span className="text-slate-100">{formatMs(value)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCauses = () => {
    if (!results?.causes?.length) return null;

    return (
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-400">
          {language === 'cs' ? 'Identifikované příčiny' : 'Identified Causes'}
        </div>
        <div className="space-y-2">
          {results.causes.map((cause, i) => {
            const label = causeLabels[cause.cause]?.[language] || cause.cause;
            
            return (
              <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${severityColors[cause.severity]}`}>
                        {severityLabels[cause.severity]?.[language] || cause.severity}
                      </span>
                      <span className="text-sm text-white font-medium">{label}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{cause.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRecommendations = () => {
    if (!results?.recommendations?.length) return null;

    return (
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-green-400">
          {language === 'cs' ? 'Doporučení' : 'Recommendations'}
        </div>
        <div className="space-y-2">
          {results.recommendations.map((rec, i) => (
            <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="flex items-start gap-2">
                <Icons.Lightbulb size={16} className="text-yellow-400 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${severityColors[rec.priority]}`}>
                      {severityLabels[rec.priority]?.[language] || rec.priority}
                    </span>
                    <span className="text-sm text-white font-medium">{rec.title}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{rec.description}</p>
                  {rec.fix && (
                    <pre className="mt-2 text-xs text-cyan-300 bg-slate-900/50 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                      {rec.fix}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMultiPageSection = () => {
    if (!hasDeepAccess) return null;

    return (
      <div className="pt-4 border-t border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-purple-400 flex items-center gap-2">
            <Icons.Layers size={14} />
            {language === 'cs' ? 'Multi-page TTFB analýza' : 'Multi-page TTFB Analysis'}
          </div>
        </div>

        {!multiPageResults ? (
          <button
            onClick={runMultiPageAnalysis}
            disabled={multiPageLoading}
            className="w-full py-2 px-3 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-400 text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {multiPageLoading ? (
              <>
                <Icons.Loader size={14} className="animate-spin" />
                {language === 'cs' ? 'Analyzuji více stránek...' : 'Analyzing multiple pages...'}
              </>
            ) : (
              <>
                <Icons.Layers size={14} />
                {language === 'cs' ? 'Analyzovat TTFB napříč webem (až 10 stránek)' : 'Analyze TTFB across site (up to 10 pages)'}
              </>
            )}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-lg font-bold text-purple-400">{formatMs(multiPageResults.summary?.avgTTFB)}</div>
                <div className="text-xs text-slate-400">{language === 'cs' ? 'Průměr' : 'Average'}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-lg font-bold text-green-400">{formatMs(multiPageResults.summary?.minTTFB)}</div>
                <div className="text-xs text-slate-400">{language === 'cs' ? 'Minimum' : 'Best'}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-lg font-bold text-red-400">{formatMs(multiPageResults.summary?.maxTTFB)}</div>
                <div className="text-xs text-slate-400">{language === 'cs' ? 'Maximum' : 'Worst'}</div>
              </div>
            </div>

            <div className="text-xs text-slate-400">
              {language === 'cs' 
                ? `Analyzováno ${multiPageResults.pagesAnalyzed} stránek`
                : `Analyzed ${multiPageResults.pagesAnalyzed} pages`}
            </div>

            {multiPageResults.summary?.worstPages?.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500">
                  {language === 'cs' ? 'Nejpomalejší stránky:' : 'Slowest pages:'}
                </div>
                {multiPageResults.summary.worstPages.map((page, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-slate-800/30 border border-slate-700/50">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => togglePageExpanded(idx)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`text-sm font-bold ${getTTFBStatus(page.ttfb).color}`}>
                          {formatMs(page.ttfb)}
                        </span>
                        <span className="text-xs text-slate-400 break-all" title={page.url}>
                          {page.url}
                        </span>
                      </div>
                      <Icons.ChevronDown 
                        size={14} 
                        className={`text-slate-400 transition-transform ${expandedPages[idx] ? 'rotate-180' : ''}`} 
                      />
                    </div>
                    
                    {expandedPages[idx] && page.causes?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-700/50 space-y-1">
                        {page.causes.map((cause, cIdx) => (
                          <div key={cIdx} className="flex items-center gap-2 text-xs">
                            <span className={`w-1.5 h-1.5 rounded-full ${severityColors[cause.severity]?.split(' ')[0] || 'bg-slate-500'}`} />
                            <span className="text-slate-300">
                              {causeLabels[cause.cause]?.[language] || cause.cause}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="text-center py-4 text-slate-400 text-sm">
        {language === 'cs' ? 'Pro TTFB diagnostiku se přihlaste' : 'Sign in for TTFB diagnostics'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!results ? (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-start gap-3">
              <Icons.Server size={20} className="text-blue-400 mt-0.5" />
              <div>
                <div className="text-sm text-white font-medium">
                  {language === 'cs' ? 'Hloubková TTFB analýza' : 'Deep TTFB Analysis'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {language === 'cs' 
                    ? 'Analyzuje dobu odpovědi serveru, DNS, TCP, TLS a identifikuje bottlenecky v síťové vrstvě.'
                    : 'Analyzes server response time, DNS, TCP, TLS and identifies network layer bottlenecks.'}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={runDiagnostics}
            disabled={loading}
            className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                <Icons.Loader size={16} className="animate-spin" />
                {language === 'cs' ? 'Analyzuji TTFB...' : 'Analyzing TTFB...'}
              </>
            ) : (
              <>
                <Icons.Search size={16} />
                {language === 'cs' ? 'Spustit hloubkovou TTFB analýzu' : 'Run Deep TTFB Analysis'}
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1.5 rounded-lg ${getTTFBStatus(results.ttfb).bg}`}>
                <span className={`text-lg font-bold ${getTTFBStatus(results.ttfb).color}`}>
                  TTFB: {formatMs(results.ttfb)}
                </span>
              </div>
              <span className="text-xs text-slate-400">
                {language === 'cs' ? 'Cíl: pod 800ms' : 'Target: under 800ms'}
              </span>
            </div>
            <button
              onClick={resetResults}
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
            >
              <Icons.RefreshCw size={12} />
              {language === 'cs' ? 'Znovu' : 'Re-run'}
            </button>
          </div>

          {results.protocol && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-400">{language === 'cs' ? 'Protokol:' : 'Protocol:'}</span>
              <span className={`px-2 py-0.5 rounded ${results.protocol.includes('h2') || results.protocol.includes('h3') ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {results.protocol.toUpperCase()}
              </span>
            </div>
          )}

          <DiagnosticEvidenceView result={results} metric="TTFB" language={language} />
          {renderPhaseBar()}
          {renderCauses()}
          {renderRecommendations()}
          {renderMultiPageSection()}

          <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-700">
            {language === 'cs' 
              ? `Analýza dokončena za ${(results.duration / 1000).toFixed(1)}s`
              : `Analysis completed in ${(results.duration / 1000).toFixed(1)}s`}
          </div>
        </div>
      )}
      
      {showUpgradeModal && (
        <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-slate-900 border-2 border-purple-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-purple-500/20" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Icons.Zap size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'cs' ? 'Hloubková TTFB analýza' : 'Deep TTFB Analysis'}
              </h3>
              <p className="text-slate-400 mb-4">
                {language === 'cs' 
                  ? 'Hloubkové analýzy jsou dostupné pouze pro tarify Premium a Enterprise.'
                  : 'Deep analyses are available only for Premium and Enterprise plans.'}
              </p>
              <div className="space-y-2">
                <a href="/pricing" className="block w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium transition-all">
                  {language === 'cs' ? 'Zobrazit tarify' : 'View Plans'}
                </a>
                <button onClick={() => setShowUpgradeModal(false)} className="w-full py-2 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm transition-all">
                  {language === 'cs' ? 'Zavřít' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TTFBDiagnosticsPanel;
